import { types } from "mobx-state-tree";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
// TODO: This is really locale specific, we need a better way to handle
// locale date identification. It isn't clear if dayjs supports this.
// In particular some locales put the day first instead of the month.
// We can just let dayjs check all of its known formats but then it
// will match any string with a date anywhere in it. There is
// a locale format plugin which is close but is probably still too
// strict.
// See data-types.test.ts to see examples
const dayjsFormats = [
    "M/D/YY",
    "M/D/YYYY",
    "M/DD/YY",
    "M/DD/YYYY",
    "MM/D/YY",
    "MM/D/YYYY",
    "MM/DD/YY",
    "MM/DD/YYYY",
    "M-D",
    "M-D-YY",
    "M-D-YYYY",
    "M-DD",
    "M-DD-YY",
    "M-DD-YYYY",
    "MM-D",
    "MM-D-YY",
    "MM-D-YYYY",
    "MM-DD",
    "MM-DD-YY",
    "MM-DD-YYYY",
    "MMMD",
    "MMMD,YY",
    "MMMD,YYYY",
    "MMMDD",
    "MMMDD,YY",
    "MMMDD,YYYY",
    "MMMMD",
    "MMMMD,YY",
    "MMMMD,YYYY",
    "MMMMDD",
    "MMMMDD,YY",
    "MMMMDD,YYYY"
];
export const ValueType = types.union(types.number, types.string, types.undefined);
export function getCellId(cell) {
    // Create a new one to ensure that the members are in a consistent order
    const orderedCell = { attributeId: cell.attributeId, caseId: cell.caseId };
    return JSON.stringify(orderedCell);
}
export function getCellFromId(cellId) {
    const cell = JSON.parse(cellId);
    if (cell.attributeId && cell.caseId)
        return cell;
}
export function uniqueCaseIds(cells) {
    const caseIds = new Set();
    cells.forEach(cell => caseIds.add(cell.caseId));
    return Array.from(caseIds);
}
export function isNumeric(val) {
    return !isNaN(toNumeric(val));
}
function handleFractions(value) {
    if (typeof value !== "string") {
        return value;
    }
    const result = value.match(/^ *(-?) *(\d+) *\/ *(\d+) *$/);
    if (result) {
        const numericValue = Number(result[2]) / Number(result[3]);
        return result[1] === "-" ? -numericValue : numericValue;
    }
    return value;
}
export function toNumeric(value) {
    if (value == null || value === "")
        return NaN;
    // Strip commas
    // HACK: This approach is not safe for internationalization.
    // Some locales use `,` for a decimal place.
    // One place to start down that rabbit hole is:
    // https://stackoverflow.com/questions/11665884/how-can-i-parse-a-string-with-a-comma-thousand-separator-to-a-number
    // The approach is also not safe because it would convert `1,2` to the number 12
    const commasStripped = typeof value === "string" ? value.replace(/,/g, "") : value;
    // Handle fractions like 1/4.
    const fractionHandled = handleFractions(commasStripped);
    return Number(fractionHandled);
}
export function isDate(val) {
    // Technically a plain number can be timestamp but for this purpose we only
    // care about non numeric string dates
    if (typeof val !== "string" || isNumeric(val)) {
        return false;
    }
    // We need to use strict matching so extra characters in the string aren't
    // completely ignored. However this also means that spaces are not allowed
    // For now we just strip the spaces. This might cause problems with some
    // formats.
    const stripped = val.replace(/ /g, "");
    return dayjs(stripped, dayjsFormats, true).isValid();
}
export function dateFrom(val) {
    return dayjs(val, dayjsFormats, true);
}
export function isImageUrl(val) {
    if (typeof val !== "string") {
        return false;
    }
    // Look for strings matching the URL for a user uploaded image, like:
    // ccimg://fbrtdb.concord.org/devclass/-NcP-LmubeWUdANUM_vO
    // Or that match more generic image URL strings, like:
    // https://concord.org/image.png
    // The ImageMap has an isImageUrl function but it will pickup pretty much any http URL.
    // And we don't want to add a dependency on the imageMap to this shared code.
    const ccImagePattern = /^ccimg:\/\/fbrtdb\.concord\.org/;
    const imageUrlPattern = /(^(https?:\/\/)?[^:]*\/[^:]*\.(gif|jpg|jpeg|png)$)|(^data:image\/(gif|jpg|jpeg|png);base64,)/i;
    return ccImagePattern.test(val) || imageUrlPattern.test(val);
}
//# sourceMappingURL=data:application/json;base64,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